import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Copy,
  ExternalLink,
  Eye,
  History,
  Inbox,
  Link2,
  Loader2,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  WalletCards,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { copyTextToClipboard } from "@/lib/clipboard";

const STATUS_FILTERS = [
  { value: "needs_collection", label: "Needs collection", Icon: WalletCards },
  { value: "proof_submitted", label: "Proofs to review", Icon: CheckCircle2 },
  { value: "partially_paid", label: "Partly paid", Icon: CircleDollarSign },
  { value: "closed", label: "Closed history", Icon: History, secondary: true },
  { value: "all", label: "All records", Icon: Inbox },
];

const COLLECTIBLE_STATUSES = new Set(["open", "partially_paid"]);
const CLOSED_STATUSES = new Set(["paid", "written_off", "denied", "reversed"]);

const SORT_OPTIONS = [
  { value: "oldest", label: "Oldest first" },
  { value: "newest", label: "Newest first" },
  { value: "highest", label: "Highest amount" },
  { value: "lowest", label: "Lowest amount" },
];

const STATUS_CONTENT = {
  open: { label: "Payment due", tone: "bg-rose-50 text-rose-700", Icon: AlertTriangle },
  partially_paid: { label: "Partly paid", tone: "bg-amber-50 text-amber-800", Icon: CircleDollarSign },
  paid: { label: "Paid", tone: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  written_off: { label: "Written off", tone: "bg-slate-100 text-slate-700", Icon: History },
  denied: { label: "Denied", tone: "bg-rose-50 text-rose-700", Icon: AlertTriangle },
  reversed: { label: "Reversed", tone: "bg-amber-50 text-amber-800", Icon: History },
};

const AR_TUTORIAL_RECEIVABLE_ID = "tutorial-ar-receivable";
const AR_TUTORIAL_PATIENT_ID = "tutorial-ar-patient";
const AR_TUTORIAL_AGING = [{
  payer_type: "patient",
  payer_name: "Maria Santos",
  current_centavos: 450000,
  total_centavos: 450000,
}];
const AR_TUTORIAL_DETAILS = [{
  ar_receivable_id: AR_TUTORIAL_RECEIVABLE_ID,
  ar_number: "AR-DEMO-001",
  source: "standalone_underpayment",
  payer_type: "patient",
  payer_name: "Maria Santos",
  patient_id: AR_TUTORIAL_PATIENT_ID,
  amount_centavos: 450000,
  status: "open",
  age_days: 18,
}];
const AR_TUTORIAL_PATIENTS = {
  [AR_TUTORIAL_PATIENT_ID]: {
    id: AR_TUTORIAL_PATIENT_ID,
    patient_number: "PT-DEMO-014",
    first_name: "Maria",
    last_name: "Santos",
    email: "maria.santos@example.test",
    phone: "09170000014",
  },
};
const AR_TUTORIAL_ORIGINS = {
  [AR_TUTORIAL_RECEIVABLE_ID]: {
    procedure_name: "Root Canal Treatment",
    service_date: "2026-07-03",
  },
};

function formatMoney(centavos, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency ? `${currency} ` : "";
  return `${symbol}${Math.round((Number(centavos) || 0) / 100).toLocaleString("en-PH")}`;
}

function formatDaysOutstanding(days) {
  const value = Math.max(0, Number(days || 0));
  return `${value} day${value === 1 ? "" : "s"}`;
}

function formatOriginDate(dateValue) {
  if (!dateValue) return "";
  const datePart = String(dateValue).split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return String(dateValue);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function sourceLabel(source) {
  const label = String(source || "AR").replace(/_/g, " ").trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : "AR";
}

function emptyStateCopy(status) {
  if (status === "closed") {
    return {
      title: "No closed balances yet",
      body: "Paid, written-off, denied, and reversed balances will stay here for reference.",
    };
  }
  if (status === "needs_collection") {
    return {
      title: "No patients need collection",
      body: "This branch has no open or partly paid patient balances.",
    };
  }
  if (status === "partially_paid") {
    return {
      title: "No partly paid balances",
      body: "Balances will appear here after a patient makes a partial payment.",
    };
  }
  if (status === "proof_submitted") {
    return {
      title: "No payment proofs to review",
      body: "New patient uploads will appear here before staff confirms payment.",
    };
  }
  return {
    title: "No balances found",
    body: "Try another search or collection filter.",
  };
}

function patientInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "PT";
}

function collectionAction(row) {
  if (row?.payment_link_status === "proof_submitted") return "Review payment proof";
  if (COLLECTIBLE_STATUSES.has(row?.normalized_status)) return "Record payment";
  return "View payment history";
}

function formatArNumber(row) {
  if (row?.ar_number) return row.ar_number;
  const id = String(row?.ar_receivable_id || "");
  return id ? `AR-${id.slice(0, 8).toUpperCase()}` : "AR";
}

function patientName(patient) {
  if (!patient) return "";
  const full = `${patient.first_name || ""} ${patient.last_name || ""}`.trim();
  return full || patient.full_name || "";
}

function uniqueValues(values) {
  return [...new Set((values || []).filter(Boolean))];
}

function mapById(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    if (row?.id) acc[row.id] = row;
    return acc;
  }, {});
}

function firstText(...values) {
  return values.find((value) => typeof value === "string" && value.trim())?.trim() || "";
}

function buildPaymentLinkUrl(payload) {
  const rawPath = firstText(
    payload?.payment_url,
    payload?.url,
    payload?.payment_path,
    payload?.paymentPath,
    payload?.token ? `/pay/${payload.token}` : "",
  );
  if (!rawPath) return "";
  try {
    return new URL(rawPath, globalThis.location?.origin || "http://localhost").toString();
  } catch {
    return rawPath;
  }
}

async function fetchByIds(table, select, ids) {
  const nextIds = uniqueValues(ids);
  if (!nextIds.length) return [];
  const { data, error } = await supabase.from(table).select(select).in("id", nextIds);
  if (error) return [];
  return Array.isArray(data) ? data : [];
}

async function loadArOriginMap(arIds) {
  const originRows = await fetchByIds(
    "ar_receivables",
    "id, service_date, aging_start_date, money_event_id, procedure_instance_id, procedure_visit_id, treatment_plan_item_id, metadata",
    arIds,
  );
  if (!originRows.length) return {};

  const moneyEvents = await fetchByIds(
    "money_events",
    "id, event_date, procedure_id, procedure_instance_id, procedure_visit_id, metadata",
    originRows.map((row) => row.money_event_id),
  );
  const moneyEventMap = mapById(moneyEvents);

  const procedureVisits = await fetchByIds(
    "procedure_visits",
    "id, treatment_plan_item_id, scheduled_at, completed_at, procedure_instance_id",
    [
      ...originRows.map((row) => row.procedure_visit_id),
      ...moneyEvents.map((row) => row.procedure_visit_id),
    ],
  );
  const procedureVisitMap = mapById(procedureVisits);

  const procedureInstances = await fetchByIds(
    "procedure_instances",
    "id, display_name, catalog_procedure_id",
    [
      ...originRows.map((row) => row.procedure_instance_id),
      ...moneyEvents.map((row) => row.procedure_instance_id),
      ...procedureVisits.map((row) => row.procedure_instance_id),
    ],
  );
  const procedureInstanceMap = mapById(procedureInstances);

  const treatmentPlanItems = await fetchByIds(
    "treatment_plan_items",
    "id, name_snapshot, procedure_id",
    [
      ...originRows.map((row) => row.treatment_plan_item_id),
      ...procedureVisits.map((row) => row.treatment_plan_item_id),
    ],
  );
  const treatmentPlanItemMap = mapById(treatmentPlanItems);

  const procedures = await fetchByIds(
    "procedures",
    "id, name, code",
    [
      ...moneyEvents.map((row) => row.procedure_id),
      ...treatmentPlanItems.map((row) => row.procedure_id),
      ...procedureInstances.map((row) => row.catalog_procedure_id),
    ],
  );
  const procedureMap = mapById(procedures);

  return originRows.reduce((acc, arRow) => {
    const moneyEvent = moneyEventMap[arRow.money_event_id] || {};
    const procedureVisit = procedureVisitMap[arRow.procedure_visit_id || moneyEvent.procedure_visit_id] || {};
    const procedureInstance = procedureInstanceMap[
      arRow.procedure_instance_id || moneyEvent.procedure_instance_id || procedureVisit.procedure_instance_id
    ] || {};
    const treatmentPlanItem = treatmentPlanItemMap[arRow.treatment_plan_item_id || procedureVisit.treatment_plan_item_id] || {};
    const procedure = procedureMap[moneyEvent.procedure_id]
      || procedureMap[treatmentPlanItem.procedure_id]
      || procedureMap[procedureInstance.catalog_procedure_id]
      || {};
    const procedureName = firstText(
      moneyEvent.metadata?.custom_name,
      arRow.metadata?.custom_name,
      procedureInstance.display_name,
      treatmentPlanItem.name_snapshot,
      procedure.name,
    );
    const serviceDate = firstText(
      arRow.service_date,
      arRow.aging_start_date,
      moneyEvent.event_date,
      procedureVisit.completed_at,
      procedureVisit.scheduled_at,
    );
    acc[arRow.id] = {
      procedure_name: procedureName,
      service_date: serviceDate,
    };
    return acc;
  }, {});
}

function originProcedureLabel(row) {
  return firstText(row?.origin_procedure_name, row?.procedure_name, sourceLabel(row?.source));
}

function originDateLabel(row) {
  return formatOriginDate(firstText(row?.origin_service_date, row?.service_date, row?.aging_start_date));
}

function formatProofDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function paymentMethodLabel(row) {
  return firstText(
    row?.payment_link_qr_label,
    row?.payment_link_qr_method ? sourceLabel(row.payment_link_qr_method) : "",
    row?.payment_link_delivery_channel ? sourceLabel(row.payment_link_delivery_channel) : "",
    "Digital payment",
  );
}

function extractPaymentReference(text) {
  const normalized = String(text || "").replace(/\s+/g, " ");
  const patterns = [
    /\b(\d{4}[\s-]\d{3}[\s-]\d{6})\b/,
    /\b(\d{4}[\s-]\d{4}[\s-]\d{4})\b/,
    /\bRef(?:\.|erence)?[\s:#]*([A-Z0-9-]{8,})\b/i,
    /\b(\d{12,16})\b/,
  ];
  for (const re of patterns) {
    const match = normalized.match(re);
    if (match) return match[1].replace(/\s/g, "");
  }
  return "";
}

export default function ARCenter() {
  const { activeClinic } = useAuth();
  const { branches, activeBranch, activeBranchId, loading: branchLoading } = useBranch();
  const branchId = activeBranchId || "";
  const [aging, setAging] = useState([]);
  const [details, setDetails] = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [paymentLinkMap, setPaymentLinkMap] = useState({});
  const [originMap, setOriginMap] = useState({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("needs_collection");
  const [sort, setSort] = useState("oldest");
  const [selectedArId, setSelectedArId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collectRow, setCollectRow] = useState(null);
  const [collectAmount, setCollectAmount] = useState(0);
  const [collectMethod, setCollectMethod] = useState("gcash");
  const [collectReference, setCollectReference] = useState("");
  const [collectNotes, setCollectNotes] = useState("");
  const [sendRow, setSendRow] = useState(null);
  const [sendChannel, setSendChannel] = useState("email");
  const [sendRecipient, setSendRecipient] = useState("");
  const [sendRecipientLocked, setSendRecipientLocked] = useState(false);
  const [sendGeneratedUrl, setSendGeneratedUrl] = useState("");
  const [proofRow, setProofRow] = useState(null);
  const [proofReference, setProofReference] = useState("");
  const [proofOcrRunning, setProofOcrRunning] = useState(false);
  const [proofOcrSuggestion, setProofOcrSuggestion] = useState("");
  const [originRow, setOriginRow] = useState(null);
  const [actionBusy, setActionBusy] = useState("");
  const proofOcrTriedRef = useRef("");
  const tutorialDemoMode = (() => {
    const params = new URLSearchParams(globalThis.location?.search || "");
    return params.get("tutorial") === "ar-center" && params.get("demo") === "1";
  })();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    if (tutorialDemoMode) {
      setAging(AR_TUTORIAL_AGING);
      setDetails(AR_TUTORIAL_DETAILS);
      setPatientMap(AR_TUTORIAL_PATIENTS);
      setOriginMap(AR_TUTORIAL_ORIGINS);
      setPaymentLinkMap({});
      setLoading(false);
      return;
    }
    if (!branchId) {
      setAging([]);
      setDetails([]);
      setPatientMap({});
      setOriginMap({});
      setPaymentLinkMap({});
      setLoading(Boolean(branchLoading));
      setError(branchLoading ? "" : "No active branch selected.");
      return;
    }
    const [agingRes, detailsRes] = await Promise.all([
      supabase.rpc("list_ar_aging", { p_branch_id: branchId }),
      supabase.rpc("list_ar_event_details", { p_branch_id: branchId }),
    ]);
    if (agingRes.error || detailsRes.error) {
      setAging([]);
      setDetails([]);
      setError(agingRes.error?.message || detailsRes.error?.message || "Could not load Accounts Receivable.");
      setLoading(false);
      return;
    }
    const nextAging = Array.isArray(agingRes.data) ? agingRes.data : [];
    const nextDetails = Array.isArray(detailsRes.data) ? detailsRes.data : [];
    const patientIds = [...new Set(nextDetails.map((row) => row.patient_id).filter(Boolean))];
    let nextPatientMap = {};
    if (patientIds.length > 0) {
      const { data: patients } = await supabase
        .from("patients")
        .select("id, patient_number, first_name, last_name, email, phone")
        .in("id", patientIds);
      nextPatientMap = (Array.isArray(patients) ? patients : []).reduce((acc, patient) => {
        acc[patient.id] = patient;
        return acc;
      }, {});
    }
    const arIds = nextDetails.map((row) => row.ar_receivable_id).filter(Boolean);
    const nextOriginMap = await loadArOriginMap(arIds);
    let nextPaymentLinkMap = {};
    if (arIds.length > 0) {
      const { data: links } = await supabase
        .from("payment_links")
        .select("id, ar_receivable_id, status, proof_url, token, recipient_email, recipient_phone, delivery_channel, amount_centavos, currency, qr_method, qr_label, proof_submitted_at, reference_number")
        .in("ar_receivable_id", arIds);
      nextPaymentLinkMap = (Array.isArray(links) ? links : [])
        .filter((link) => ["pending", "proof_submitted"].includes(String(link.status || "")))
        .reduce((acc, link) => {
          acc[link.ar_receivable_id] = link;
          return acc;
        }, {});
    }
    setAging(nextAging);
    setDetails(nextDetails);
    setPatientMap(nextPatientMap);
    setOriginMap(nextOriginMap);
    setPaymentLinkMap(nextPaymentLinkMap);
    setLoading(false);
  }, [branchId, branchLoading, tutorialDemoMode]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!proofRow) {
      setProofOcrRunning(false);
      setProofOcrSuggestion("");
      proofOcrTriedRef.current = "";
      return undefined;
    }
    if (!proofRow.proof_url) return undefined;
    if (proofRow.payment_link_status !== "proof_submitted") return undefined;
    if (proofReference.trim()) return undefined;
    const ocrKey = proofRow.payment_link_id || proofRow.proof_url;
    if (!ocrKey || proofOcrTriedRef.current === ocrKey) return undefined;
    proofOcrTriedRef.current = ocrKey;
    let cancelled = false;
    setProofOcrRunning(true);
    setProofOcrSuggestion("");
    (async () => {
      try {
        const Tesseract = await import("tesseract.js");
        const { data } = await Tesseract.recognize(proofRow.proof_url, "eng");
        if (cancelled) return;
        const detected = extractPaymentReference(data?.text);
        if (detected) setProofOcrSuggestion(detected);
      } catch (ocrError) {
        // Non-blocking: staff can always type the reference manually.
        // eslint-disable-next-line no-console
        console.warn("[ar-proof-ocr] failed", ocrError);
      }
      if (!cancelled) setProofOcrRunning(false);
    })();
    return () => { cancelled = true; };
  }, [proofReference, proofRow]);

  const currency = activeClinic?.currency || "PHP";
  const branchOptions = useMemo(() => (
    (Array.isArray(branches) ? branches : [])
      .filter((branch) => branch?.id && branch?.name)
      .map((branch) => ({ id: branch.id, name: branch.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  ), [branches]);

  const enrichedRows = useMemo(() => details.map((row) => {
    const patient = patientMap[row.patient_id] || {};
    const paymentLink = paymentLinkMap[row.ar_receivable_id] || {};
    const origin = originMap[row.ar_receivable_id] || {};
    const name = patientName(patient) || row.patient_name || row.payer_name || "Unknown patient";
    const number = patient.patient_number || row.patient_number || "No patient #";
    return {
      ...row,
      patient_name: name,
      patient_number: number,
      patient_email: patient.email || row.patient_email || "",
      patient_phone: patient.phone || row.patient_phone || "",
      ar_number: formatArNumber(row),
      days_outstanding: Number(row.age_days || row.days_outstanding || 0),
      amount: Number(row.amount_centavos || 0),
      normalized_status: String(row.status || "open").toLowerCase(),
      payment_link_id: row.payment_link_id || paymentLink.id || null,
      payment_link_status: row.payment_link_status || paymentLink.status || null,
      proof_url: row.proof_url || paymentLink.proof_url || null,
      proof_submitted_at: row.proof_submitted_at || paymentLink.proof_submitted_at || null,
      payment_link_reference_number: row.reference_number || paymentLink.reference_number || "",
      payment_link_amount_centavos: row.payment_link_amount_centavos || paymentLink.amount_centavos || row.amount_centavos || 0,
      payment_link_currency: row.payment_link_currency || paymentLink.currency || currency,
      payment_link_qr_method: row.payment_link_qr_method || paymentLink.qr_method || "",
      payment_link_qr_label: row.payment_link_qr_label || paymentLink.qr_label || "",
      payment_link_delivery_channel: row.payment_link_delivery_channel || paymentLink.delivery_channel || "",
      payment_link_token: row.payment_link_token || paymentLink.token || null,
      origin_procedure_name: origin.procedure_name || row.origin_procedure_name || row.procedure_name || "",
      origin_service_date: origin.service_date || row.origin_service_date || row.service_date || row.aging_start_date || "",
    };
  }), [currency, details, patientMap, paymentLinkMap, originMap]);

  const openRows = useMemo(() => (
    enrichedRows.filter((row) => COLLECTIBLE_STATUSES.has(row.normalized_status))
  ), [enrichedRows]);

  const statusCounts = useMemo(() => enrichedRows.reduce((acc, row) => {
    acc.all += 1;
    if (COLLECTIBLE_STATUSES.has(row.normalized_status)) acc.needs_collection += 1;
    if (row.payment_link_status === "proof_submitted") acc.proof_submitted += 1;
    if (row.normalized_status === "partially_paid") acc.partially_paid += 1;
    if (CLOSED_STATUSES.has(row.normalized_status)) acc.closed += 1;
    return acc;
  }, { all: 0, needs_collection: 0, proof_submitted: 0, partially_paid: 0, closed: 0 }), [enrichedRows]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return enrichedRows
      .filter((row) => {
        if (status === "all") return true;
        if (status === "needs_collection") return COLLECTIBLE_STATUSES.has(row.normalized_status);
        if (status === "proof_submitted") return row.payment_link_status === "proof_submitted";
        if (status === "closed") return CLOSED_STATUSES.has(row.normalized_status);
        return row.normalized_status === status;
      })
      .filter((row) => {
        if (!query) return true;
        return [
          row.ar_number,
          row.patient_name,
          row.patient_number,
          originProcedureLabel(row),
          originDateLabel(row),
          row.source,
          row.payer_name,
        ].some((value) => String(value || "").toLowerCase().includes(query));
      })
      .sort((a, b) => {
        if (sort === "newest") return a.days_outstanding - b.days_outstanding;
        if (sort === "highest") return b.amount - a.amount;
        if (sort === "lowest") return a.amount - b.amount;
        return b.days_outstanding - a.days_outstanding;
      });
  }, [enrichedRows, search, sort, status]);

  useEffect(() => {
    if (!visibleRows.length) {
      setSelectedArId("");
      return;
    }
    if (!visibleRows.some((row) => row.ar_receivable_id === selectedArId)) {
      setSelectedArId(visibleRows[0].ar_receivable_id);
    }
  }, [selectedArId, visibleRows]);

  const selectedRow = useMemo(() => (
    visibleRows.find((row) => row.ar_receivable_id === selectedArId) || visibleRows[0] || null
  ), [selectedArId, visibleRows]);

  const totals = useMemo(() => {
    const byPayer = aging.reduce((sum, row) => sum + Number(row.total_centavos || 0), 0);
    const byRows = openRows.reduce((sum, row) => sum + Number(row.amount_centavos || 0), 0);
    return {
      totalCentavos: byPayer || byRows,
      rowCount: openRows.length,
      proofCount: openRows.filter((row) => row.payment_link_status === "proof_submitted").length,
    };
  }, [aging, openRows]);

  const emptyCopy = emptyStateCopy(status);

  function openCollect(row) {
    setCollectRow(row);
    setCollectAmount(Number(row.amount_centavos || 0));
    setCollectMethod("gcash");
    setCollectReference(tutorialDemoMode ? "GCASH-DEMO-4500" : "");
    setCollectNotes("");
  }

  function openProofReview(row) {
    setProofRow(row);
    setProofReference("");
    setProofOcrRunning(false);
    setProofOcrSuggestion("");
    proofOcrTriedRef.current = "";
  }

  function openSendLink(row) {
    const emailOnFile = firstText(row?.patient_email);
    setSendRow(row);
    setSendChannel("email");
    setSendRecipient(emailOnFile);
    setSendRecipientLocked(Boolean(emailOnFile));
    setSendGeneratedUrl("");
  }

  function handleSendChannelChange(channel) {
    setSendChannel(channel);
    setSendGeneratedUrl("");
    if (channel === "email") {
      const emailOnFile = firstText(sendRow?.patient_email);
      setSendRecipient(emailOnFile);
      setSendRecipientLocked(Boolean(emailOnFile));
      return;
    }
    if (channel === "sms") {
      setSendRecipient(firstText(sendRow?.patient_phone));
      setSendRecipientLocked(false);
      return;
    }
    setSendRecipient("");
    setSendRecipientLocked(false);
  }

  async function handleCollectConfirm() {
    if (!collectRow || actionBusy) return;
    if (collectAmount <= 0 || collectAmount > Number(collectRow.amount_centavos || 0)) {
      toast.error("Enter a valid AR payment amount.");
      return;
    }
    if (collectMethod !== "cash" && !collectReference.trim()) {
      toast.error("Reference number is required.");
      return;
    }
    if (tutorialDemoMode) {
      toast.success("Training complete. No payment was recorded.");
      setCollectRow(null);
      return;
    }
    setActionBusy("collect");
    const { error: rpcError } = await supabase.rpc("collect_patient_ar_payment", {
      p_ar_receivable_id: collectRow.ar_receivable_id,
      p_amount_centavos: collectAmount,
      p_method: collectMethod,
      p_reference_number: collectReference.trim() || null,
      p_notes: collectNotes.trim() || null,
      p_idempotency_key: globalThis.crypto?.randomUUID?.() || `ar-collect-${Date.now()}`,
    });
    setActionBusy("");
    if (rpcError) {
      toast.error(rpcError.message || "Could not record AR payment.");
      return;
    }
    toast.success("AR payment recorded.");
    setCollectRow(null);
    await load();
  }

  async function handleSendLinkConfirm() {
    if (!sendRow || actionBusy) return;
    setActionBusy("send");
    const { data, error: rpcError } = await supabase.rpc("generate_ar_payment_link", {
      p_ar_receivable_id: sendRow.ar_receivable_id,
      p_channel: sendChannel,
      p_recipient: sendChannel === "copy" ? null : sendRecipient.trim() || null,
    });
    setActionBusy("");
    if (rpcError) {
      toast.error(rpcError.message || "Could not generate AR payment link.");
      return;
    }
    const nextUrl = buildPaymentLinkUrl(data);
    if (sendChannel === "copy") {
      setSendGeneratedUrl(nextUrl);
      toast.success("AR payment link created. Copy it below.");
      await load();
      return;
    }
    toast.success("AR payment link created.");
    setSendRow(null);
    await load();
  }

  async function handleCopyGeneratedLink() {
    if (!sendGeneratedUrl) {
      toast.error("Create the payment link first.");
      return;
    }
    try {
      await copyTextToClipboard(sendGeneratedUrl);
      toast.success("Payment link copied.");
    } catch {
      toast.error("Could not copy payment link.");
    }
  }

  async function handleProofConfirm() {
    if (!proofRow || actionBusy) return;
    if (!proofReference.trim()) {
      toast.error("Reference number is required.");
      return;
    }
    setActionBusy("proof");
    const { error: rpcError } = await supabase.rpc("verify_payment_link", {
      p_link_id: proofRow.payment_link_id,
      p_reference: proofReference.trim(),
    });
    setActionBusy("");
    if (rpcError) {
      toast.error(rpcError.message || "Could not verify payment proof.");
      return;
    }
    toast.success("Payment proof confirmed.");
    setProofRow(null);
    await load();
  }

  if (loading && !aging.length && !details.length) {
    return <div data-testid="ar-center-loading" className="p-5 text-[13px] text-slate-500">Loading Accounts Receivable…</div>;
  }

  if (error) {
    return (
      <div data-testid="ar-center-error" className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-[13px] text-rose-700">
        <div className="flex items-center gap-2 font-bold">
          <AlertTriangle className="h-4 w-4" strokeWidth={2.25} />
          Accounts Receivable could not load
        </div>
        <p className="mt-1">{error}</p>
      </div>
    );
  }

  return (
    <main data-testid="ar-center-page" data-tutorial-id="ar-center-page" className="space-y-5 text-slate-950">
      <header className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold tracking-tight text-slate-950">Accounts Receivable</h1>
          <p className="mt-1 text-[14px] text-slate-500">See who owes, why, and what to do next.</p>
        </div>
        <button
          type="button"
          data-testid="ar-center-refresh"
          onClick={load}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 transition hover:border-teal-300 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
        >
          <RefreshCw className={["h-4 w-4", loading ? "animate-spin" : ""].join(" ")} strokeWidth={2.25} />
          Refresh
        </button>
      </header>

      <CollectionsTodaySummary totals={totals} currency={currency} loading={loading} />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <ArTabs selected={status} onSelect={setStatus} counts={statusCounts} />
        <div className="text-[12px] text-slate-500">
          {visibleRows.length.toLocaleString()} of {enrichedRows.length.toLocaleString()} records · {activeBranch?.name || branchOptions.find((branch) => branch.id === branchId)?.name || "Selected branch"}
        </div>
      </div>

      <section className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onInput={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search patient, procedure, AR number, or date"
            data-testid="ar-center-search"
            className="min-h-11 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
          />
        </div>
        <select
          value={sort}
          onChange={(event) => setSort(event.target.value)}
          data-testid="ar-center-sort"
          aria-label="Sort collection register"
          className="min-h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]" data-testid="ar-center-open-items" data-tutorial-id="ar-center-open-items">
        <ArRegister
          rows={visibleRows}
          selectedArId={selectedRow?.ar_receivable_id || ""}
          onSelect={setSelectedArId}
          currency={currency}
          emptyCopy={emptyCopy}
        />
        <ArSidecar
          row={selectedRow}
          currency={currency}
          onCollect={openCollect}
          onReviewProof={openProofReview}
          onSendLink={openSendLink}
          onViewOrigin={setOriginRow}
        />
      </section>

      {collectRow ? (
        <ActionDialog testid="ar-collect-dialog" tutorialId="ar-center-collect-dialog" title="Collect AR payment" onClose={() => setCollectRow(null)}>
          <p className="text-sm font-semibold text-slate-900">{collectRow.ar_number}</p>
          <p className="mt-1 text-xs text-slate-500">Collects against this AR item only. Today's visit bill and production credit stay separate.</p>
          <label data-tutorial-id="ar-center-collect-amount" className="mt-4 block text-xs font-bold text-slate-600">
            Amount
            <input
              data-testid="ar-collect-amount"
              type="number"
              min="0"
              value={Math.round(collectAmount / 100)}
              onChange={(event) => setCollectAmount(Math.max(0, Math.round(Number(event.target.value || 0) * 100)))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <div data-tutorial-id="ar-center-collect-details" className="mt-3 space-y-3">
          <label className="block text-xs font-bold text-slate-600">
            Method
            <select
              data-testid="ar-collect-method"
              value={collectMethod}
              onChange={(event) => setCollectMethod(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="gcash">GCash</option>
              <option value="maya">Maya</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="cash">Cash</option>
            </select>
          </label>
          <label className="block text-xs font-bold text-slate-600">
            Reference
            <input
              data-testid="ar-collect-reference"
              value={collectReference}
              onChange={(event) => setCollectReference(event.target.value)}
              onInput={(event) => setCollectReference(event.currentTarget.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-xs font-bold text-slate-600">
            Note
            <textarea
              value={collectNotes}
              onChange={(event) => setCollectNotes(event.target.value)}
              className="mt-1 min-h-20 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          </div>
          <DialogActions
            busy={actionBusy === "collect"}
            confirmLabel="Record payment"
            onCancel={() => setCollectRow(null)}
            onConfirm={handleCollectConfirm}
            confirmTestId="ar-collect-confirm"
            confirmTutorialId="ar-center-collect-confirm"
          />
        </ActionDialog>
      ) : null}

      {sendRow ? (
        <ActionDialog testid="ar-send-link-dialog" title="Send AR payment link" onClose={() => setSendRow(null)}>
          <p className="text-sm font-semibold text-slate-900">{sendRow.ar_number} · {formatMoney(sendRow.amount_centavos, currency)}</p>
          <p className="mt-1 text-xs text-slate-500">The link requests the current full AR amount. Uploaded proof still needs staff approval.</p>
          <label className="mt-4 block text-xs font-bold text-slate-600">
            Channel
            <select
              data-testid="ar-send-link-channel"
              value={sendChannel}
              onChange={(event) => handleSendChannelChange(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="copy">Create link only</option>
            </select>
          </label>
          {sendChannel !== "copy" ? (
            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between gap-3">
                <label htmlFor="ar-send-link-recipient" className="text-xs font-bold text-slate-600">Recipient</label>
                {sendChannel === "email" && sendRecipientLocked ? (
                  <button
                    type="button"
                    data-testid="ar-send-link-change-recipient"
                    onClick={() => setSendRecipientLocked(false)}
                    className="text-xs font-bold text-teal-700 hover:text-teal-900"
                  >
                    Change
                  </button>
                ) : null}
              </div>
              <input
                id="ar-send-link-recipient"
                data-testid="ar-send-link-recipient"
                value={sendRecipient}
                readOnly={sendRecipientLocked}
                onChange={(event) => setSendRecipient(event.target.value)}
                onInput={(event) => setSendRecipient(event.currentTarget.value)}
                placeholder={sendChannel === "email" ? "No email on file" : "Mobile number"}
                className={[
                  "w-full rounded-md border border-slate-300 px-3 py-2 text-sm",
                  sendRecipientLocked ? "bg-slate-50 text-slate-600" : "bg-white text-slate-900",
                ].join(" ")}
              />
              {sendChannel === "email" ? (
                <p className="mt-1 text-[11px] font-semibold text-slate-500">
                  {sendRecipientLocked ? "Email on file" : "Editable for this link only"}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
              Create a payment link for staff to copy and paste into any message.
            </div>
          )}
          {sendGeneratedUrl ? (
            <div className="mt-3">
              <label htmlFor="ar-send-link-copy-url" className="text-xs font-bold text-slate-600">Payment link</label>
              <div className="mt-1 flex gap-2">
                <input
                  id="ar-send-link-copy-url"
                  data-testid="ar-send-link-copy-url"
                  value={sendGeneratedUrl}
                  readOnly
                  className="min-w-0 flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                <button
                  type="button"
                  onClick={handleCopyGeneratedLink}
                  className="inline-flex h-10 items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-3 text-xs font-bold text-teal-800 hover:bg-teal-100"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
              </div>
            </div>
          ) : null}
          <DialogActions
            busy={actionBusy === "send"}
            confirmLabel={sendChannel === "copy" ? "Create link" : "Send link"}
            onCancel={() => setSendRow(null)}
            onConfirm={handleSendLinkConfirm}
            confirmTestId="ar-send-link-confirm"
          />
        </ActionDialog>
      ) : null}

      {proofRow ? (
        <ActionDialog testid="ar-proof-review-dialog" title="Review AR payment proof" onClose={() => setProofRow(null)} size="lg">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">{proofRow.ar_number}</p>
              <p className="mt-1 text-xs text-slate-500">Full amount required before AR closes. Confirmation posts the payment and allocation.</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Pending review
            </span>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3" data-testid="ar-proof-review-panel">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_260px]">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">Patient proof</p>
                  <p className="text-xs text-slate-500">{paymentMethodLabel(proofRow)}</p>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  {proofRow.proof_url ? (
                    <a href={proofRow.proof_url} target="_blank" rel="noopener" className="block bg-white">
                      <img
                        src={proofRow.proof_url}
                        alt="Patient proof"
                        data-testid="ar-proof-preview"
                        className="max-h-[420px] w-full object-contain"
                      />
                    </a>
                  ) : (
                    <div className="flex min-h-56 flex-col items-center justify-center p-8 text-center text-slate-500">
                      <Link2 className="h-8 w-8 text-slate-300" />
                      <p className="mt-3 text-sm font-semibold text-slate-700">No proof image attached</p>
                      <p className="mt-1 text-xs">Ask the patient to upload the receipt again before verifying.</p>
                    </div>
                  )}
                </div>
                {proofRow.proof_url ? (
                  <a
                    href={proofRow.proof_url}
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:underline"
                  >
                    Open full receipt <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : null}
              </div>

              <aside className="space-y-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Claimed amount</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">
                    {formatMoney(proofRow.payment_link_amount_centavos || proofRow.amount_centavos, proofRow.payment_link_currency || currency)}
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 p-4 text-sm">
                  <ProofInfoLine label="Uploaded" value={formatProofDateTime(proofRow.proof_submitted_at)} />
                  <ProofInfoLine label="AR origin" value={originProcedureLabel(proofRow)} />
                  <ProofInfoLine label="Date" value={originDateLabel(proofRow) || "Not linked"} />
                  <ProofInfoLine label="Patient" value={`${proofRow.patient_name} · ${proofRow.patient_number}`} />
                  <ProofInfoLine label="Reference" value={proofRow.payment_link_reference_number || "Needs entry below"} />
                </div>
              </aside>
            </div>
          </div>

          <div className="mt-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="ar-proof-verify-panel">
            <div>
              <p className="text-sm font-bold text-amber-950">Verify & close this AR</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-900">
                Compare the receipt with the claimed amount, enter the transaction reference, then verify. The database posts the payment and allocation before the AR closes.
              </p>
            </div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600" htmlFor="ar-proof-reference">
              Reference / transaction # from receipt
              <input
                id="ar-proof-reference"
                data-testid="ar-proof-reference"
                value={proofReference}
                onChange={(event) => setProofReference(event.target.value)}
                onInput={(event) => setProofReference(event.currentTarget.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-900 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
            </label>
            {proofOcrRunning ? (
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Reading the screenshot…
              </div>
            ) : proofOcrSuggestion && proofOcrSuggestion !== proofReference ? (
              <button
                type="button"
                onClick={() => setProofReference(proofOcrSuggestion)}
                data-testid="ar-proof-ocr-suggestion"
                className="inline-flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
              >
                <Sparkles className="h-4 w-4 text-amber-600" />
                <span>Auto-detected reference number:</span>
                <span className="font-mono font-bold">{proofOcrSuggestion}</span>
                <span className="font-normal text-amber-700">Tap to use</span>
              </button>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setProofRow(null)} disabled={actionBusy === "proof"} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" data-testid="ar-proof-confirm" onClick={handleProofConfirm} disabled={actionBusy === "proof" || !proofReference.trim()} className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-teal-700 px-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-60">
                <CheckCircle2 className="h-4 w-4" />
                {actionBusy === "proof" ? "Saving..." : "Verify & close AR"}
              </button>
            </div>
          </div>
        </ActionDialog>
      ) : null}

      {originRow ? (
        <ActionDialog testid="ar-origin-panel" title="AR origin" onClose={() => setOriginRow(null)}>
          <p className="text-sm font-semibold text-slate-900">{originRow.ar_number}</p>
          <dl className="mt-4 space-y-3 text-sm">
            <OriginRow label="Source" value={originProcedureLabel(originRow)} />
            <OriginRow label="Date" value={originDateLabel(originRow)} />
            <OriginRow label="Patient" value={`${originRow.patient_name} · ${originRow.patient_number}`} />
            <OriginRow label="Amount" value={formatMoney(originRow.amount_centavos, currency)} />
          </dl>
        </ActionDialog>
      ) : null}
    </main>
  );
}

function CollectionsTodaySummary({ totals, currency, loading }) {
  const items = [
    {
      label: "Outstanding balance",
      value: formatMoney(totals.totalCentavos, currency),
      Icon: WalletCards,
    },
    {
      label: "Balances to collect",
      value: Number(totals.rowCount || 0).toLocaleString(),
      Icon: CircleDollarSign,
    },
    {
      label: "Proofs to review",
      value: Number(totals.proofCount || 0).toLocaleString(),
      Icon: CheckCircle2,
    },
  ];
  return (
    <section
      data-testid="ar-center-summary"
      data-tutorial-id="ar-center-summary"
      aria-label="AR summary"
      aria-busy={loading}
      className="overflow-hidden rounded-xl bg-teal-700 text-teal-50 shadow-sm shadow-teal-950/10"
    >
      <div className="px-5 pt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-teal-100">Collections today</div>
      <div className="grid sm:grid-cols-3">
        {items.map(({ label, value, Icon }, index) => (
          <div
            key={label}
            className={[
              "flex min-h-[92px] items-center gap-3 px-5 py-4",
              index ? "border-t border-teal-500/50 sm:border-l sm:border-t-0" : "",
              loading ? "opacity-60" : "",
            ].join(" ")}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-600 text-teal-50">
              <Icon className="h-5 w-5" strokeWidth={2} />
            </span>
            <span>
              <span className="block text-[24px] font-bold leading-none tabular-nums text-white">{value}</span>
              <span className="mt-1.5 block text-[13px] font-medium text-teal-50">{label}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ArTabs({ selected, onSelect, counts }) {
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2" aria-label="AR status filters" data-testid="ar-center-status-tabs" data-tutorial-id="ar-center-status-tabs">
      {STATUS_FILTERS.map((option) => {
        const active = selected === option.value;
        const Icon = option.Icon;
        return (
          <button
            type="button"
            key={option.value}
            data-testid={`ar-tab-${option.value}`}
            aria-pressed={active}
            onClick={() => onSelect(option.value)}
            className={[
              "inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-[13px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600",
              option.secondary ? "lg:ml-auto" : "",
              active
                ? "border-teal-600 bg-teal-50 text-teal-800"
                : "border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:text-teal-800",
            ].join(" ")}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
            {option.label}
            <span className={[
              "min-w-5 rounded-md px-1.5 py-0.5 text-center text-[11px] tabular-nums",
              active ? "bg-white text-teal-700" : "bg-slate-100 text-slate-500",
            ].join(" ")}>
              {Number(counts[option.value] || 0).toLocaleString()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ArRegister({ rows, selectedArId, onSelect, currency, emptyCopy }) {
  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="text-[16px] font-bold text-slate-950">Collection register</h2>
        <p className="mt-0.5 text-[12px] text-slate-500">Select a patient to see the balance and next collection step.</p>
      </div>
      {rows.length ? (
        <>
          <div className="hidden grid-cols-[minmax(150px,1.1fr)_minmax(140px,1fr)_minmax(130px,0.85fr)_minmax(130px,0.8fr)_20px] gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500 md:grid">
            <span>Patient</span>
            <span>Why this exists</span>
            <span>Next step</span>
            <span>Balance</span>
            <span />
          </div>
          <div className="divide-y divide-slate-100">
            {rows.map((row) => {
              const selected = selectedArId === row.ar_receivable_id;
              const collectible = COLLECTIBLE_STATUSES.has(row.normalized_status);
              return (
                <button
                  key={row.ar_receivable_id}
                  type="button"
                  data-testid={`ar-register-row-${row.ar_receivable_id}`}
                  data-tutorial-id={row.ar_receivable_id === AR_TUTORIAL_RECEIVABLE_ID ? "ar-center-demo-row" : undefined}
                  aria-pressed={selected}
                  onClick={() => onSelect(row.ar_receivable_id)}
                  className={[
                    "grid w-full gap-3 px-4 py-4 text-left transition md:grid-cols-[minmax(150px,1.1fr)_minmax(140px,1fr)_minmax(130px,0.85fr)_minmax(130px,0.8fr)_20px] md:items-center",
                    selected ? "bg-teal-50/70 ring-1 ring-inset ring-teal-200" : "hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-bold text-slate-950">{row.patient_name}</span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">{row.patient_number}</span>
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-semibold text-slate-800">{originProcedureLabel(row)}</span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">{originDateLabel(row) || row.ar_number}</span>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-slate-900">{collectionAction(row)}</span>
                    <span className="mt-1 block text-[11px] text-slate-500">
                      {collectible
                        ? `${formatDaysOutstanding(row.days_outstanding)} unpaid${row.payment_link_status === "pending" ? " · Link sent" : ""}`
                        : row.ar_number}
                    </span>
                  </span>
                  <span className="min-w-0">
                    <ArStatus row={row} />
                    <span className="mt-1 block text-[13px] font-bold tabular-nums text-slate-950">{formatMoney(row.amount_centavos, currency)}</span>
                  </span>
                  <ChevronRight className="hidden h-4 w-4 text-slate-300 md:block" strokeWidth={2} />
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div className="px-4 py-14 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-teal-50 text-teal-700">
            <WalletCards className="h-5 w-5" strokeWidth={2} />
          </span>
          <p className="mt-3 text-sm font-bold text-slate-800">{emptyCopy.title}</p>
          <p className="mt-1 text-xs text-slate-500">{emptyCopy.body}</p>
        </div>
      )}
      <div className="border-t border-slate-100 px-4 py-3 text-xs font-semibold text-slate-500">
        {rows.length} {rows.length === 1 ? "record" : "records"} shown
      </div>
    </section>
  );
}

function ArSidecar({ row, currency, onCollect, onReviewProof, onSendLink, onViewOrigin }) {
  if (!row) {
    return (
      <aside data-testid="ar-center-sidecar" data-tutorial-id="ar-center-sidecar" className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-[13px] font-medium text-slate-500">
        Select a patient to see the next collection step.
      </aside>
    );
  }
  const collectible = COLLECTIBLE_STATUSES.has(row.normalized_status);
  const proofWaiting = row.payment_link_status === "proof_submitted";
  const primaryLabel = proofWaiting ? "Review payment proof" : collectible ? "Record payment" : "View AR details";
  return (
    <aside data-testid="ar-center-sidecar" data-tutorial-id="ar-center-sidecar" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-700 text-[13px] font-bold text-white">
            {patientInitials(row.patient_name)}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[18px] font-bold text-slate-950">{row.patient_name}</h2>
            <p className="mt-0.5 truncate text-[12px] text-slate-500">{row.patient_number}</p>
            <div className="mt-2"><ArStatus row={row} /></div>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Next step</div>
          <div className="mt-1.5 text-[20px] font-bold leading-6 text-slate-950">{primaryLabel}</div>
          <div className="mt-1 text-[12px] text-slate-500">
            {collectible
              ? `${formatMoney(row.amount_centavos, currency)} still unpaid for ${formatDaysOutstanding(row.days_outstanding)}.`
              : "This balance is closed and kept for payment history."}
          </div>
          {proofWaiting ? (
            <button
              type="button"
              data-testid={`ar-center-review-proof-${row.ar_receivable_id}`}
              onClick={() => onReviewProof(row)}
              className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 text-[13px] font-bold text-white transition hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              <CheckCircle2 className="h-4 w-4" strokeWidth={2.25} />
              Review payment proof
            </button>
          ) : collectible ? (
            <button
              type="button"
              data-testid={`ar-center-collect-${row.ar_receivable_id}`}
              data-tutorial-id={row.ar_receivable_id === AR_TUTORIAL_RECEIVABLE_ID ? "ar-center-record-payment" : undefined}
              onClick={() => onCollect(row)}
              className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 text-[13px] font-bold text-white transition hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              <WalletCards className="h-4 w-4" strokeWidth={2.25} />
              Record payment
            </button>
          ) : (
            <button
              type="button"
              data-testid={`ar-center-closed-origin-${row.ar_receivable_id}`}
              onClick={() => onViewOrigin(row)}
              className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 text-[13px] font-bold text-white transition hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              <Eye className="h-4 w-4" strokeWidth={2.25} />
              View AR details
            </button>
          )}
        </div>
      </div>

      <section className="border-t border-slate-100 px-5 py-5">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Balance details</h3>
        <div className="mt-3 space-y-2.5">
          <SidecarMetric label={collectible ? "Amount still unpaid" : "Recorded amount"} value={formatMoney(row.amount_centavos, currency)} danger={collectible} />
          <SidecarMetric label="Waiting" value={collectible ? formatDaysOutstanding(row.days_outstanding) : "Closed"} />
          <SidecarMetric label="Status" value={STATUS_CONTENT[row.normalized_status]?.label || "Recorded"} />
          {row.payment_link_status ? (
            <SidecarMetric label="Payment link" value={proofWaiting ? "Proof uploaded" : "Sent"} />
          ) : null}
        </div>
      </section>

      <section className="border-t border-slate-100 px-5 py-5">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Why this balance exists</h3>
        <div className="mt-3 space-y-2.5">
          <SidecarMetric label="Treatment" value={originProcedureLabel(row)} />
          <SidecarMetric label="Date" value={originDateLabel(row) || "Not linked"} />
          <SidecarMetric label="AR number" value={row.ar_number} />
        </div>
        <button
          type="button"
          data-testid={`ar-origin-link-${row.ar_receivable_id}`}
          onClick={() => onViewOrigin(row)}
          className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-bold text-slate-700 transition hover:border-teal-300 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
        >
          <Eye className="h-4 w-4" strokeWidth={2.25} />
          View balance details
        </button>
      </section>

      {collectible ? (
        <div className="space-y-2 border-t border-slate-100 px-5 py-5">
          {proofWaiting ? (
            <button
              type="button"
              data-testid={`ar-center-collect-${row.ar_receivable_id}`}
              onClick={() => onCollect(row)}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-bold text-slate-700 transition hover:border-teal-300 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              <WalletCards className="h-4 w-4" strokeWidth={2.25} />
              Record direct payment
            </button>
          ) : (
            <button
              type="button"
              data-testid={`ar-center-send-link-${row.ar_receivable_id}`}
              onClick={() => onSendLink(row)}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-bold text-slate-700 transition hover:border-teal-300 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              <Send className="h-4 w-4" strokeWidth={2.25} />
              Create payment link
            </button>
          )}
        </div>
      ) : null}
    </aside>
  );
}

function ArStatus({ row }) {
  const content = row.payment_link_status === "proof_submitted"
    ? { label: "Proof waiting", tone: "bg-amber-50 text-amber-800", Icon: CheckCircle2 }
    : STATUS_CONTENT[row.normalized_status] || { label: "Recorded", tone: "bg-slate-100 text-slate-700", Icon: History };
  const Icon = content.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-bold ${content.tone}`}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
      {content.label}
    </span>
  );
}

function SidecarMetric({ label, value, danger = false }) {
  return (
    <div className="flex items-start justify-between gap-3 text-[12px]">
      <span className="text-slate-500">{label}</span>
      <span className={`max-w-[62%] text-right font-semibold ${danger ? "text-rose-700" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function ActionDialog({ title, testid, tutorialId = null, children, onClose, size = "md" }) {
  const widthClass = size === "lg" ? "max-w-3xl" : "max-w-md";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4">
      <section data-testid={testid} data-tutorial-id={tutorialId || undefined} className={`w-full ${widthClass} max-h-[92vh] overflow-hidden rounded-lg bg-white shadow-xl`}>
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-bold text-slate-950">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="max-h-[calc(92vh-57px)] overflow-y-auto px-5 py-4">{children}</div>
      </section>
    </div>
  );
}

function DialogActions({ busy, confirmLabel, onCancel, onConfirm, confirmTestId, confirmTutorialId = null }) {
  return (
    <div className="mt-5 flex justify-end gap-2">
      <button type="button" onClick={onCancel} disabled={busy} className="h-9 rounded-md border border-slate-200 px-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
        Cancel
      </button>
      <button type="button" data-testid={confirmTestId} data-tutorial-id={confirmTutorialId || undefined} onClick={onConfirm} disabled={busy} className="h-9 rounded-md bg-teal-700 px-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-60">
        {busy ? "Saving..." : confirmLabel}
      </button>
    </div>
  );
}

function OriginRow({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 break-words font-semibold text-slate-900">{value || "Not linked"}</dd>
    </div>
  );
}

function ProofInfoLine({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{value || "Not linked"}</span>
    </div>
  );
}
